import { RoomRecord } from "common/records/Room";
import { RootState } from "common/reducers";
import { List } from "immutable";
import { createSelector } from "reselect";
import { currentMeasurement } from "common/selectors/measurement.common.selectors";
import { recordChanged } from "common/helpers/Record";

export const rootKey = "rooms";

/**
 * Returns a Room record
 *
 * @param {RootState} state
 * @param {{ roomId: number }} options
 * @returns {RoomRecord}
 */
export const room = (state: RootState, props: any) => state.getIn([rootKey, "byId", props.roomId]);

export const lastSavedRoom = (state: RootState, props: any) => state.getIn([rootKey, "lastSavedById", props.roomId]);

/**
 * Returns a list of Room records
 *
 * @param {RootState} state
 * @param {{ roomIds: number }} options
 * @returns {List<RoomRecord>}
 */
export const rooms = createSelector([(state, props) => ({ state, props })], (stateProps: any): List<RoomRecord> => {
  const { state, props } = stateProps;
  const { roomIds } = props;
  return roomIds?.map((id: number) => room(state, { roomId: id }));
});

/**
 * Gets the last saved rooms for the given ids
 *
 * @param {RootState} state The RootState
 * @param {{}} props The props
 * @returns {List<RoomRecord>} lastSavedRooms
 */
export const lastSavedRooms = (state: RootState, props: any) => {
  return props?.roomIds?.map((id: number) => lastSavedRoom(state, { roomId: id }));
};

/**
 * Gets the rooms for the current measurement
 *
 * @param {RootState} state The RootState
 * @param {{}} options NONE
 * @returns {List<DoorRecord>} doors
 */
export const currentMeasurementRooms = createSelector(
  [currentMeasurement, (state: RootState) => state],
  (measurement, state) => {
    return rooms(state, { roomIds: measurement?.get("room_ids") || List() });
  },
);

/**
 * Gets the last saved rooms for the current measurement
 *
 * @param {RootState} state The RootState
 * @param {{}} options NONE
 * @returns {List<RoomRecord>} lastSavedRooms
 */
export const currentMeasurementLastSavedRooms = createSelector(
  [currentMeasurement, (state: RootState) => state],
  (measurement, state) => {
    return lastSavedRooms(state, { roomIds: measurement?.get("room_ids") || List() });
  },
);

/**
 * Checks if the rooms have changed on the current measurement
 *
 * @param {RootState} state RootState
 * @param {{}} options NONE
 * @returns {boolean} True if the rooms have changed, false otherwise
 */
export const roomsChanged = (state: RootState): boolean => {
  return recordChanged(currentMeasurementRooms(state), currentMeasurementLastSavedRooms(state));
};
